'use client';

import { useEffect } from 'react';
import { create } from 'zustand';

import { useInstallations } from '@/data/installations/use-installations';

type PrClientStoreProps = {
  owner: string;
  repo: string;
  number: string;
};

type PrStore = {
  owner: string;
  repo: string;
  number: string;
  installationId: number | null;
  repositoryId: number | null;
  pullRequestId: string | null;
  isInitialized: boolean;
  setOwner: (input: string) => void;
  setRepo: (input: string) => void;
  setNumber: (input: string) => void;
  setInstallationId: (input: number | null) => void;
  setRepositoryId: (input: number | null) => void;
  setPullRequestId: (input: string | null) => void;
  setInitialized: (initialized: boolean) => void;
};

type PrStoreContext = {
  owner: string;
  repo: string;
  number: string;
  installationId: number | null;
  repositoryId?: number | null;
  pullRequestId?: string | null;
};

export const usePrStore = create<PrStore>((set) => ({
  owner: '',
  repo: '',
  number: '',
  installationId: null,
  repositoryId: null,
  pullRequestId: null,
  isInitialized: false,
  setOwner: (input: string) => set({ owner: input }),
  setRepo: (input: string) => set({ repo: input }),
  setNumber: (input: string) => set({ number: input }),
  setInstallationId: (input: number | null) => set({ installationId: input }),
  setRepositoryId: (input: number | null) => set({ repositoryId: input }),
  setPullRequestId: (input: string | null) => set({ pullRequestId: input }),
  setInitialized: (initialized: boolean) => set({ isInitialized: initialized }),
}));

/**
 * Hook to wait for PR store initialization
 * Returns true if the store is initialized with valid values
 */
export function usePrStoreReady() {
  const { owner, repo, number, isInitialized } = usePrStore();
  return isInitialized && owner !== '' && repo !== '' && number !== '';
}

export function initializePrStoreContext({
  owner,
  repo,
  number,
  installationId,
  repositoryId = null,
  pullRequestId = null,
}: PrStoreContext): void {
  const store = usePrStore.getState();
  store.setOwner(owner);
  store.setRepo(repo);
  store.setNumber(number);
  store.setInstallationId(installationId);
  store.setRepositoryId(repositoryId);
  store.setPullRequestId(pullRequestId);
  store.setInitialized(true);
}

export function cleanupPrStoreContext({
  owner,
  repo,
  number,
}: Pick<PrStoreContext, 'owner' | 'repo' | 'number'>): void {
  const currentState = usePrStore.getState();
  // Guard against stale cleanups from previous route transitions mutating the active PR store.
  if (
    currentState.owner === owner &&
    currentState.repo === repo &&
    currentState.number === number
  ) {
    currentState.setOwner('');
    currentState.setRepo('');
    currentState.setNumber('');
    currentState.setInstallationId(null);
    currentState.setRepositoryId(null);
    currentState.setPullRequestId(null);
    currentState.setInitialized(false);
  }
}

export function PrClientStore({ owner, repo, number }: PrClientStoreProps) {
  const { data: installations } = useInstallations();

  useEffect(() => {
    const currentInstallation = installations?.find((installation) => {
      return installation.accountLogin.toLowerCase() === owner.toLowerCase();
    });

    initializePrStoreContext({
      owner,
      repo,
      number,
      installationId: currentInstallation?.installationId ?? null,
      pullRequestId: null,
    });

    return () => {
      cleanupPrStoreContext({ owner, repo, number });
    };
  }, [owner, repo, number, installations]);

  return null;
}
